import { getMinPrice, getTotalPrice } from '@shared/lib/money';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
	BaggageType,
	CheckoutOrderFragment,
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment,
	OrderAdditionalServiceGdsServiceServiceType,
	OrderAdditionalServicesFragment,
	OrderSelectedServices,
	OrderStatus,
	SelectedInsuranceFragment
} from '@websky/graphql';
import { Props as CardExtraProps } from './components/CardExtra';
import { useCartPrices } from '../Cart/hooks';
import {
	CardExtraGdsService,
	CardExtraInsuranceService,
	CardExtraService,
	CardExtraTransferService,
	ServiceType
} from './types';
import { Money as MoneyType } from '../Money/types';
import { getAllSegmentsServicePrice, getIsLoadingServiceByParams } from '../utils';
import {
	canBeModifyService,
	extraGdsServiceToServicePayload,
	filterGdsServicesByType,
	getCartPriceKeyByType,
	getGdsServiceTypeByServiceType,
	getPassengerPrices,
	getServicesPrices,
	getServiceVisible,
	getUnconfirmedServices,
	isSingleService as getIsSingleService
} from './utils';
import { useConfig } from '../context';
import { ServicePayload } from '../Checkout/store/selectedServices/actions';
import { useCardExtras } from './CardExtras.context';
import { getSelectedTravellerServices } from '../Checkout/store/order/selectors';
import { WEBSKY_BAGGAGE_SERVICE_OPEN_EVENT, WEBSKY_SERVICE_OPEN_EVENT } from '../Checkout/constants';
import { BaggageServiceOpenEvent, ServiceOpenEvent } from '../Checkout/types';
import { CombinedSelectedServiceSubType, SpecialServiceType } from '../AIPurchasePredictionService/types';
import { LoadingServiceType } from '../Checkout/store/loadingServicesParams/reducers';
import { getQueryPopupGoal } from '../DataLayerAnalytics/utils';
import { getSeatPrice } from '@entities/seat-map';
import { getSeatsWithDoubleSeats } from '../SeatMap/utils';

export function useCardExtra(props: CardExtraProps) {
	const { type, filter, insuranceCodes, variant, segmentId } = props;

	// Hooks
	const {
		global: {
			companyInfo: { loyaltyProgramAccountUrl }
		}
	} = useConfig();

	const {
		order,
		flight,
		segments,
		travellers,
		additionalServices,
		passengers,
		selectedServices,
		brandIncludedBaggage,
		userInfo,
		updateUser,
		isCheckinAvailable
	} = useCardExtras();

	const cardExtraService = useCardExtraService(props);
	const cardExtraDisclosure = useCardExtraDisclosure(type);
	const cardExtraPrices = useCardExtraPrices({
		type,
		insuranceCodes,
		additionalServices: cardExtraService.additionalServicesByType
	});

	// Side effects
	const onEventHandler = useCallback(
		(event: CustomEvent<ServiceOpenEvent>) => {
			if (cardExtraService.isSingleService) {
				return;
			}

			if (cardExtraDisclosure.isOpenModal) {
				cardExtraDisclosure.onCloseModal();
			}

			const {
				rfisc: eventServiceRfisc,
				type: eventServiceType,
				subType: eventServiceSubType,
				insuranceCode: eventServiceInsuranceCode
			} = event.detail.service ?? {};

			let showModal = false;

			switch (type) {
				case 'insurance':
					const insurances = cardExtraService.additionalServicesByType.insurances?.insurancePrograms;

					if (!insurances?.length) {
						return;
					}

					const insuranceCodeSet = new Set<string>(insurances.map(insurance => insurance.code));

					const firstInsuranceCode = cardExtraService.additionalServicesByType.insurances?.insuranceProgramGroups?.find(
						group => !!group.insurancePrograms?.length
					)?.insurancePrograms?.[0]?.code;

					showModal =
						eventServiceType === SpecialServiceType.Insurance &&
						(!insuranceCodeSet.size ||
							insuranceCodeSet.has(eventServiceInsuranceCode || firstInsuranceCode));
					break;
				case 'misc':
					const miscServices = cardExtraService.additionalServicesByType.gdsServices
						?.services as GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment[];

					if (!miscServices.length) {
						return;
					}

					if (eventServiceType !== cardExtraService.gdsServiceType) {
						return;
					}

					if (eventServiceRfisc || eventServiceSubType) {
						const miscServicesRfiscSet = new Set<string>();
						const miscServicesSubTypeSet = new Set<string>();

						miscServices.forEach(({ rfisc, subtype }) => {
							miscServicesRfiscSet.add(rfisc);

							// TODO: change title to correct key
							if (subtype?.title) {
								miscServicesSubTypeSet.add(subtype.title);
							}
						});

						showModal =
							miscServicesRfiscSet.has(eventServiceRfisc) ||
							miscServicesSubTypeSet.has(eventServiceSubType);
					} else {
						const miscServicesCount = additionalServices.gdsServices.services.reduce(
							(total, service) =>
								total + (service.type === OrderAdditionalServiceGdsServiceServiceType.Misc ? 1 : 0),
							0
						);

						showModal = miscServicesCount === 1;
					}

					break;
				default:
					const gdsServices = cardExtraService.additionalServicesByType.gdsServices?.services;

					if (!gdsServices?.length) {
						return;
					}

					if (eventServiceType !== cardExtraService.gdsServiceType) {
						return;
					}

					const servicesRfiscSet = new Set();
					const serviceSubTypeSet = new Set<CombinedSelectedServiceSubType>();

					gdsServices.forEach(service => {
						servicesRfiscSet.add(service.rfisc);

						if ((service as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment)?.baggageType) {
							// BaggageService
							const baggageService = service as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment;

							serviceSubTypeSet.add(baggageService.baggageType);
						} else if ((service as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment)?.subType) {
							// Vip service
							const vipService = service as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment;

							serviceSubTypeSet.add(vipService.subType);
						}
					});

					showModal = servicesRfiscSet.has(eventServiceRfisc) || serviceSubTypeSet.has(eventServiceSubType);

					break;
			}

			if (!showModal) {
				return;
			}

			event.stopImmediatePropagation();
			cardExtraDisclosure.onOpenModal();

			let afterEvent: CustomEvent;

			if (type === 'baggage' && eventServiceSubType) {
				afterEvent = new CustomEvent<BaggageServiceOpenEvent>(WEBSKY_BAGGAGE_SERVICE_OPEN_EVENT, {
					detail: { type: eventServiceSubType as BaggageType }
				});
			} else if (type === 'misc' && eventServiceSubType && eventServiceRfisc) {
				afterEvent = new CustomEvent<ServiceOpenEvent>(WEBSKY_SERVICE_OPEN_EVENT, {
					detail: {
						service: {
							type: eventServiceType,
							rfisc: eventServiceRfisc,
							insuranceCode: eventServiceInsuranceCode
						},
						meta: {
							custom_ecommerce_name: getQueryPopupGoal(
								eventServiceType as OrderAdditionalServiceGdsServiceServiceType,
								undefined,
								eventServiceInsuranceCode,
								eventServiceRfisc
							)
						},
						callback: async () => {}
					}
				});
			}

			setTimeout(() => document.dispatchEvent(afterEvent));
		},
		[
			cardExtraService.isSingleService,
			cardExtraService.gdsServiceType,
			cardExtraService.additionalServicesByType,
			cardExtraDisclosure.isOpenModal,
			cardExtraDisclosure.onOpenModal,
			cardExtraDisclosure.onCloseModal
		]
	);

	useEffect(() => {
		document.addEventListener(WEBSKY_SERVICE_OPEN_EVENT, onEventHandler as EventListener);

		return () => document.removeEventListener(WEBSKY_SERVICE_OPEN_EVENT, onEventHandler as EventListener);
	}, []);

	// Methods
	const onSaveServicesRequest = useCallback<typeof cardExtraService.onSaveServicesRequest>(
		async serviceIds => {
			const data = await cardExtraService.onSaveServicesRequest(serviceIds);

			cardExtraDisclosure.onCloseModal();

			return data;
		},
		[cardExtraService.onSaveServicesRequest, cardExtraDisclosure.onCloseModal]
	);

	const onToggleService = useCallback(async () => {
		if (!cardExtraService.isSingleService) {
			return;
		}

		if (type === 'insurance') {
			const insuranceCode = cardExtraService.additionalServicesByType.insurances.insurancePrograms[0]?.code;
			const isSelected = additionalServices.insurances.selectedInsurances?.some(
				insurance => insurance.insuranceProgram.code === insuranceCode
			);

			if (isSelected) {
				await cardExtraService.onRemoveServices([{ code: insuranceCode }]);
			} else {
				await cardExtraService.onAddServices([{ code: insuranceCode }]);
			}
		} else if (type === 'transfer') {
			const tripId = cardExtraService.additionalServicesByType.transfer.aeroexpress.trips[0].id;
			const isSelected = cardExtraService.additionalServicesByType.transfer.aeroexpress?.selectedTrips?.some(
				trip => trip.id === tripId
			);

			if (isSelected) {
				await cardExtraService.onRemoveServices([{ id: tripId }]);
			} else {
				await cardExtraService.onAddServices([{ id: tripId }]);
			}
		} else {
			const service = cardExtraService.additionalServicesByType.gdsServices?.services?.[0];

			if (!service) {
				return;
			}

			const selectedServiceIds = new Set<string>();
			const travellerIds = new Set<string>();
			const segmentIds = new Set<string>(
				service.allowedSegments.reduce((segments, ids) => [...segments, ...ids], [])
			);

			travellers.forEach(traveller => {
				travellerIds.add(traveller.id);
				traveller.services?.gdsServices?.services?.forEach(service =>
					selectedServiceIds.add(service.serviceId)
				);
			});

			const isSelected = selectedServiceIds.has(service.id);
			const services = [...travellerIds].map(travellerId => ({
				serviceId: service.id,
				passengerId: travellerId,
				segmentIds: [...segmentIds],
				count: isSelected ? 0 : 1
			}));

			await (isSelected ? cardExtraService.onRemoveServices(services) : cardExtraService.onAddServices(services));
		}
	}, [type, additionalServices, cardExtraService.isSingleService, cardExtraService.additionalServicesByType]);

	const onActionClose = useCallback(
		<T extends CardExtraService>(request: (gdsServices: T[]) => Promise<void> | void) => async (
			gdsServices: T[]
		) => {
			await request(gdsServices);
			cardExtraDisclosure.onCloseModal();
		},
		[cardExtraDisclosure.onCloseModal]
	);

	const onViewLoyaltyStatus = useCallback(() => {
		const loyaltyUrl = loyaltyProgramAccountUrl ?? '/account/loyalty';
		window.open(loyaltyUrl, '_blank');
	}, [loyaltyProgramAccountUrl]);

	const isReadonly = useMemo(() => {
		if (type !== 'seats') {
			return false;
		}

		return passengers.every(passenger => {
			const seatsWithDoubleSeats = getSeatsWithDoubleSeats(passenger.seats);

			return passenger.seats.every(seat => seat.isConfirmed || seatsWithDoubleSeats.has(seat.number));
		});
	}, [type, passengers]);

	return {
		// context
		order,
		flight,
		segments,
		travellers,
		additionalServices,
		passengers,
		selectedServices,
		brandIncludedBaggage,
		userInfo,
		updateUser,
		isCheckinAvailable,

		// data
		type,
		filter,
		variant,
		segmentId,
		additionalServicesByType: cardExtraService.additionalServicesByType,
		unconfirmedServices: cardExtraService.unconfirmedServices,
		gdsServiceType: cardExtraService.gdsServiceType,
		showService: cardExtraService.showService,
		isSingleService: cardExtraService.isSingleService,
		isCanModifyServices: cardExtraService.isCanModifyServices,
		isLoading: cardExtraService.isLoading,
		isConfirmed: order.status === OrderStatus.Confirmed && !cardExtraPrices?.totalPrice?.amount,
		isReadonly: isReadonly,
		cardExtraPrices,
		cardExtraDisclosure,

		// methods
		onSaveServicesRequest,
		onToggleService,
		onSaveServices: onActionClose(cardExtraService.onSaveServices),
		onAddServices: onActionClose(cardExtraService.onAddServices),
		onRemoveServices: onActionClose(cardExtraService.onRemoveServices),
		onRemoveUnconfirmedServices: cardExtraService.onRemoveUnconfirmedServices,
		onViewLoyaltyStatus
	};
}

export function useCardExtraService(props: CardExtraProps) {
	const { type, filter, insuranceCodes } = props;

	// Hooks
	const {
		global: { servicesAvailable }
	} = useConfig();

	const {
		order,
		flight,
		travellers,
		additionalServices,
		passengers,
		selectedServices,
		brandIncludedBaggage,
		loadingServiceParams,
		...cardExtras
	} = useCardExtras();

	const [isLoading, setIsLoading] = useState(false);

	// Data
	const gdsServiceType = useMemo(() => {
		return getGdsServiceTypeByServiceType(type);
	}, [type]);

	const additionalServicesByType = useMemo(() => {
		return filterGdsServicesByType(
			type,
			flight,
			additionalServices,
			insuranceCodes,
			servicesAvailable.animal,
			filter
		);
	}, [type, flight, additionalServices, insuranceCodes, servicesAvailable.animal, filter]);

	const unconfirmedServices = useMemo(() => {
		return getUnconfirmedServices(gdsServiceType, travellers, selectedServices);
	}, [gdsServiceType, travellers, selectedServices]);

	const isSingleService = useMemo(() => {
		return getIsSingleService(type, additionalServicesByType);
	}, [type, additionalServicesByType]);

	const isCanModifyServices = useMemo(() => {
		if (!additionalServices.gdsServices?.services?.length) {
			return false;
		}

		return additionalServices.gdsServices.services.some(service => canBeModifyService(service, travellers));
	}, [additionalServices, travellers]);

	const isLoadingByParams = useMemo(() => {
		if (!loadingServiceParams) {
			return false;
		}

		const isInsuranceType = type === 'insurance';
		const isTransferType = type === 'transfer';
		const isInsuranceOrTransfer = isInsuranceType || isTransferType;

		return getIsLoadingServiceByParams(
			loadingServiceParams,
			isInsuranceOrTransfer
				? { type: isInsuranceType ? LoadingServiceType.Insurance : LoadingServiceType.Transfer }
				: additionalServicesByType.gdsServices?.services?.[0]
		);
	}, [type, loadingServiceParams]);

	const showService = useMemo(() => {
		const isSupportFfp = !!(order as CheckoutOrderFragment)?.ffpInfo?.ffpSupported;

		return getServiceVisible(
			type,
			flight,
			travellers,
			additionalServicesByType,
			isCanModifyServices,
			isSupportFfp,
			{
				includedBaggageAvailable: brandIncludedBaggage.length > 0,
				animalAvailable: servicesAvailable.animal
			}
		);
	}, [
		type,
		order,
		flight,
		travellers,
		additionalServicesByType,
		brandIncludedBaggage,
		servicesAvailable.animal,
		isCanModifyServices
	]);

	// Methods
	const onSaveServicesRequest = useCallback(
		async (serviceIds: string[]) => {
			setIsLoading(true);

			const params: OrderSelectedServices = {
				id: order.id
			};

			if (type === 'insurance') {
				params.insurances = [
					{
						selectedPrograms: serviceIds,
						selectedProgramsByTravellers: travellers.map(traveller => ({
							travellerId: traveller.id,
							travellerDocumentNumber: '',
							programCodes: serviceIds
						}))
					}
				];
			} else if (type === 'transfer') {
				params.aeroexpress = {
					selectedTrips: serviceIds
				};
			}

			const response = await cardExtras.onSaveServicesRequest(params);

			setIsLoading(false);

			return response;
		},
		[type, order.id, travellers, additionalServices, cardExtras.onSaveServicesRequest]
	);

	const onActionServices = useCallback(
		async (
			actionType: 'add' | 'remove',
			servicesToSave: CardExtraService[],
			gdsServicesRequest: (services: ServicePayload[]) => void
		) => {
			if (type === 'insurance' || type === 'transfer') {
				const serviceIds = new Set<string>();

				if (type === 'insurance') {
					const insuranceServices = servicesToSave as CardExtraInsuranceService[];

					additionalServices.insurances?.selectedInsurances?.forEach(insurance =>
						serviceIds.add(insurance.insuranceProgram.code)
					);

					insuranceServices.forEach(service => {
						if (actionType === 'add') {
							serviceIds.add(service.code);
						} else {
							serviceIds.delete(service.code);
						}
					});
				} else if (type === 'transfer') {
					const transferServices = servicesToSave as CardExtraTransferService[];

					additionalServices.transfer?.aeroexpress?.selectedTrips?.forEach(trip => serviceIds.add(trip.id));

					transferServices.forEach(service => {
						if (actionType === 'add') {
							serviceIds.add(service.id);
						} else {
							serviceIds.delete(service.id);
						}
					});
				}

				await onSaveServicesRequest([...serviceIds]);
			} else {
				const gdsServices = servicesToSave as CardExtraGdsService[];
				const servicesPayload: ServicePayload[] = [];

				gdsServices.forEach(service => {
					service.segmentIds.map(segmentId => {
						const servicePayload = extraGdsServiceToServicePayload(
							{ ...service, segmentIds: [segmentId] },
							gdsServiceType
						);

						servicesPayload.push(servicePayload);
					});
				});

				gdsServicesRequest(servicesPayload);
			}
		},
		[type, gdsServiceType, onSaveServicesRequest]
	);

	const onSaveServices = useCallback(
		(gdsServices: CardExtraGdsService[]) => {
			const servicesPayload = gdsServices.map(service =>
				extraGdsServiceToServicePayload(service, gdsServiceType)
			);

			return cardExtras.onSaveServices(servicesPayload);
		},
		[cardExtras.onSaveServices]
	);

	const onAddServices = useCallback(
		(servicesToAdd: CardExtraService[]) => {
			return onActionServices('add', servicesToAdd, cardExtras.onAddServices);
		},
		[onActionServices, cardExtras.onAddServices]
	);

	const onRemoveServices = useCallback(
		(servicesToRemove: CardExtraService[]) => {
			return onActionServices('remove', servicesToRemove, cardExtras.onRemoveServices);
		},
		[onActionServices, cardExtras.onRemoveServices]
	);

	const onRemoveUnconfirmedServices = useCallback(() => cardExtras.onRemoveServices(unconfirmedServices), [
		unconfirmedServices,
		cardExtras.onRemoveServices
	]);

	return {
		// data
		additionalServicesByType,
		unconfirmedServices,
		gdsServiceType,
		isSingleService,
		isCanModifyServices,
		isLoading: isLoading || isLoadingByParams,
		showService,

		// methods
		onSaveServicesRequest,
		onSaveServices,
		onAddServices,
		onRemoveServices,
		onRemoveUnconfirmedServices
	};
}

interface UseCardExtraPricesProps {
	type: ServiceType;
	insuranceCodes?: string[];
	additionalServices: OrderAdditionalServicesFragment;
}

export function useCardExtraPrices({ type, insuranceCodes, additionalServices }: UseCardExtraPricesProps) {
	// Hooks
	const { travellers, flight } = useCardExtras();
	const prices = useCartPrices();

	// Data
	const isDifferentPrices = useMemo(() => {
		const servicesPrices = getServicesPrices(type, additionalServices);

		return new Set(servicesPrices.map(price => price.amount)).size > 1;
	}, [type, additionalServices]);

	const isAllSegmentsAvailableOnCheckin = useMemo(() => {
		return flight.seatMap.segments.every(segment => segment.isSeatsAvailableInCheckinOnly === true);
	}, [flight.seatMap]);

	const passengerPrices = useMemo(() => {
		return getPassengerPrices(type, additionalServices, travellers, isDifferentPrices);
	}, [type, additionalServices, travellers, isDifferentPrices]);

	const totalPrice = useMemo(() => {
		if (type === 'seats') {
			const seatPrices: MoneyType[] = [];

			travellers.forEach(traveller => {
				traveller.services?.seats?.forEach(seat => {
					if (seat.seat) {
						seatPrices.push(getSeatPrice(seat.seat));
					}
				});
			});

			return getTotalPrice(seatPrices, price => price);
		} else if (type === 'insurance' && insuranceCodes?.length) {
			const selectedInsuranceMap = new Map<string, SelectedInsuranceFragment>();

			if (additionalServices.insurances?.selectedInsurances?.length) {
				additionalServices.insurances.selectedInsurances.forEach(insurance => {
					const insuranceCode = insurance.insuranceProgram.code;

					if (insuranceCodes.includes(insuranceCode) && !selectedInsuranceMap.has(insuranceCode)) {
						selectedInsuranceMap.set(insuranceCode, insurance);
					}
				});
			}

			return getTotalPrice(
				[...selectedInsuranceMap.values()],
				insurance => insurance.insuranceProgram.totalPrice
			);
		} else if (type === 'misc') {
			const selectedServices = getSelectedTravellerServices(additionalServices.gdsServices.services, travellers);

			return getTotalPrice(selectedServices, service => service.price);
		}

		const priceKey = getCartPriceKeyByType(type);

		if (!priceKey) {
			return null;
		}

		return prices[priceKey] as MoneyType;
	}, [type, additionalServices, travellers, prices]);

	const minPrice = useMemo(() => {
		if (type === 'seats') {
			if (isAllSegmentsAvailableOnCheckin) {
				return null;
			}

			const seatPricesMap = new Map<number, MoneyType>();

			if (flight.seatMap?.segments?.length) {
				flight.seatMap.segments.forEach(segment => {
					segment.decks?.forEach(deck => {
						deck.rows?.forEach(row => {
							row.seats?.forEach(seat => {
								const price = seat?.price;
								const amount = price?.amount;

								if (seat?.isAvailable && amount !== undefined && !seatPricesMap.has(amount)) {
									seatPricesMap.set(amount, price);
								}
							});
						});
					});
				});
			}

			return getMinPrice([...seatPricesMap.values()], price => price);
		}

		if (type === 'sms') {
			return getAllSegmentsServicePrice(additionalServices.gdsServices?.services[0]);
		}

		return getMinPrice(passengerPrices, price => price.price);
	}, [type, passengerPrices, flight, isAllSegmentsAvailableOnCheckin]);

	return {
		isDifferentPrices,
		passengerPrices,
		totalPrice,
		minPrice
	};
}

export function useCardExtraDisclosure(type: ServiceType) {
	const [openServiceType, setOpenServiceType] = useState<ServiceType>();

	const onToggleModal = useCallback(() => {
		setOpenServiceType(state => (state ? undefined : type));
	}, [type, setOpenServiceType]);

	const onOpenModal = useCallback(() => {
		setOpenServiceType(type);
	}, [type, setOpenServiceType]);

	const onCloseModal = useCallback(() => {
		setOpenServiceType(undefined);
	}, [setOpenServiceType]);

	return {
		// data
		openServiceType,
		isOpenModal: !!openServiceType,

		// methods
		setOpenServiceType,
		onToggleModal,
		onOpenModal,
		onCloseModal
	};
}
